import React, { useMemo, useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Plus, ChevronDown, ChevronRight, Users, Truck, Bus as BusIcon, UserSearch, X } from "lucide-react";
import { api } from "@/lib/api";
import { pricingApi } from "@/lib/phase3Entities";
import { apiError } from "@/lib/errors";
import { assetDisplay, coachOptionLabel } from "@/lib/assetDisplay";
import DriverSearchPanel from "@/components/DriverSearchPanel";
import DriverCannotBeAssignedDialog from "@/components/rc10s/DriverCannotBeAssignedDialog";
import DriverTypeahead from "@/components/rc10s/DriverTypeahead";
import {
  preflight as rc10sPreflight,
  parseSaveErrorAsDiagnostic,
  newLocalCorrelationId,
} from "@/lib/driverAssignmentPreflight";

/**
 * Wave 4B-Q · CP4B — per-Bus visual grouping over the existing flat
 * pricing model. Reads server-computed `summary.unit_subtotals` +
 * `line_items` and groups by `unit_group_id` (which is the
 * tour_asset_assignment.id of the coach). Adds contextual
 *   + Add Bus  · + Add Trailer  · + Add Driver
 * actions that reuse existing tour/assignment/pricing APIs — no new
 * backend routes required.
 *
 * This component is additive: it renders ABOVE the existing PricingTab
 * editor so operators see per-Bus totals + per-Bus lines up top while
 * detailed section-based editing remains available below.
 */
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

export default function BusGroupedView({
  tour, summary, assets, drivers, allAssets, allDrivers, canWrite, canAssign, refresh,
}) {
  const tourId = tour.id;
  // Coach assignments = each is a Bus/Unit. Trailer assignments are attached to a Bus.
  const busAssignments = useMemo(
    () => assets.filter((a) => {
      const asset = allAssets.find((x) => x.id === a.asset_id);
      return !asset || asset.asset_type !== "trailer";
    }),
    [assets, allAssets],
  );
  const trailerAssignments = useMemo(
    () => assets.filter((a) => {
      const asset = allAssets.find((x) => x.id === a.asset_id);
      return asset && asset.asset_type === "trailer";
    }),
    [assets, allAssets],
  );

  const busLookup = useMemo(() => {
    const m = new Map();
    busAssignments.forEach((a) => {
      const asset = allAssets.find((x) => x.id === a.asset_id) || {};
      m.set(a.id, {
        assignment_id: a.id,
        asset_id: a.asset_id,
        display: assetDisplay(asset),
        fleet_number: asset.fleet_number,
        unit_name: asset.unit_name,
        asset,
      });
    });
    return m;
  }, [busAssignments, allAssets]);

  const linesByBus = useMemo(() => {
    const map = new Map();
    const quoteLevel = [];
    (summary?.line_items || []).forEach((li) => {
      const ug = li.unit_group_id;
      if (ug && busLookup.has(ug)) {
        if (!map.has(ug)) map.set(ug, []);
        map.get(ug).push(li);
      } else {
        quoteLevel.push(li);
      }
    });
    return { map, quoteLevel };
  }, [summary, busLookup]);

  const unitSubtotals = summary?.unit_subtotals || {};
  // H1 repair (Msg 625): compute total driver wages across the Quote for
  // an informational read-out above Payroll Fee. Sum server-computed
  // amounts on `driver_days` + `additional_driver_days` lines (Main + Co,
  // every Bus). Never recomputed from qty×rate here — trust the server.
  const totalDriverWages = useMemo(() => {
    let total = 0;
    (summary?.line_items || []).forEach((li) => {
      if (li.category === "driver_days" || li.category === "additional_driver_days") {
        const amt = Number(li.computed_amount ?? li.amount ?? 0);
        if (!Number.isNaN(amt)) total += amt;
      }
    });
    return total;
  }, [summary]);
  const [expanded, setExpanded] = useState(() => {
    // Default: first Bus expanded, others collapsed.
    const s = new Set();
    if (busAssignments[0]) s.add(busAssignments[0].id);
    return s;
  });
  const toggle = (id) => setExpanded((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const [addBusOpen, setAddBusOpen] = useState(false);
  const [findDriversOpen, setFindDriversOpen] = useState(false);

  return (
    <div className="bg-white border border-coach-silver rounded-md" data-testid="bus-grouped-view">
      <div className="flex items-baseline justify-between px-4 py-3 border-b border-coach-silver">
        <div>
          <div className="text-[13.5px] font-medium text-carbon">Quote Builder — by Bus</div>
          <div className="text-[11px] text-steel">Each Bus/Unit rolls up its own coach lease, drivers, cleaning, and custom charges. Subtotals come from server compute — never recomputed in the browser.</div>
        </div>
        <div className="flex items-center gap-2">
          {canAssign && (
            <button
              type="button"
              data-testid="bus-find-drivers-btn"
              onClick={() => setFindDriversOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] border border-coach-blue text-coach-blue rounded-sm hover:bg-coach-blue/5"
            >
              <UserSearch size={13} /> Find Drivers
            </button>
          )}
          {canAssign && (
            <button
              type="button"
              data-testid="bus-add-bus-btn"
              onClick={() => setAddBusOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] bg-coach-blue text-white rounded-sm hover:bg-deep-road-navy"
            >
              <Plus size={13} /> Add Bus
            </button>
          )}
        </div>
      </div>

      {busAssignments.length === 0 ? (
        <div className="px-4 py-6 text-[13px] text-steel italic">
          No Bus/Unit is attached to this tour yet. Add a Coach to begin building the Quote.
        </div>
      ) : (
        <div className="divide-y divide-coach-silver">
          {busAssignments.map((a) => {
            const bus = busLookup.get(a.id);
            const isOpen = expanded.has(a.id);
            const busLines = linesByBus.map.get(a.id) || [];
            const trailer = trailerAssignments[0];
            // Multiple trailers rare; we display the FIRST trailer whose assignment id matches
            // a `unit_group_id` in this Bus's line items, or fall back to the first trailer.
            const busTrailer = trailerAssignments.find((t) => busLines.some((li) => li.unit_group_id === a.id && li.category === "trailer_lease")) || trailer;
            const busDrivers = drivers.filter((d) => busLines.some((li) => li.unit_group_id === a.id && (li.category === "driver_days" || li.category === "additional_driver_days")));
            return (
              <BusCard
                key={a.id}
                bus={bus}
                open={isOpen}
                onToggle={() => toggle(a.id)}
                subtotal={unitSubtotals[a.id]}
                lines={busLines}
                trailer={busTrailer && (allAssets.find((x) => x.id === busTrailer.asset_id) || null)}
                busDrivers={busDrivers}
                allDrivers={allDrivers || []}
                tourId={tourId}
                tour={tour}
                canWrite={canWrite}
                canAssign={canAssign}
                refresh={refresh}
              />
            );
          })}
        </div>
      )}

      {linesByBus.quoteLevel.length > 0 && (
        <div className="border-t border-coach-silver bg-road-white" data-testid="quote-level-charges">
          <div className="px-4 py-2 text-[10.5px] uppercase tracking-widest text-steel">
            Quote-Level Charges
          </div>
          <table className="w-full text-[12px]">
            <tbody className="divide-y divide-coach-silver/60">
              <tr data-testid="quote-total-driver-wages">
                <td className="py-1 px-4 text-steel italic">
                  Total Driver Wages (across all Buses)
                </td>
                <td className="py-1 text-right tabular pr-4 w-32 text-steel">
                  {fmt(totalDriverWages)}
                </td>
              </tr>
            </tbody>
          </table>
          <QuoteLevelLines lines={linesByBus.quoteLevel} />
        </div>
      )}

      {addBusOpen && (
        <AddBusModal
          tour={tour}
          allAssets={allAssets}
          existingAssignedIds={busAssignments.map((a) => a.asset_id)}
          onClose={() => setAddBusOpen(false)}
          onDone={async () => { setAddBusOpen(false); await refresh(); }}
        />
      )}
      {findDriversOpen && (
        <FindDriversModal
          tour={tour}
          onClose={() => setFindDriversOpen(false)}
          refresh={refresh}
        />
      )}
    </div>
  );
}

// ---------------- Find Drivers modal (Tour-context DriverSearchPanel) ----------------
function FindDriversModal({ tour, onClose, refresh }) {
  const [assigning, setAssigning] = useState(null); // row payload
  const [pendingWarning, setPendingWarning] = useState(null);
  const [busy, setBusy] = useState(false);

  // RC10s · diagnostic dialog state + correlation-id family tracking.
  const [diagnostic, setDiagnostic] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const correlationRef = useRef(null);
  const preflightTimerRef = useRef(null);

  const runPreflight = async (row, { immediate = false } = {}) => {
    if (!row?.driver?.id) return;
    if (!tour?.id) return;
    if (!correlationRef.current || correlationRef.current.driverId !== row.driver.id) {
      correlationRef.current = {
        driverId: row.driver.id,
        correlationId: newLocalCorrelationId(),
      };
    }
    const run = async () => {
      const diag = await rc10sPreflight(
        tour.id, row.driver.id, "primary_driver",
        correlationRef.current?.correlationId,
      );
      // Keep server-returned correlation_id if newer.
      if (diag.correlation_id) {
        correlationRef.current = {
          driverId: row.driver.id,
          correlationId: diag.correlation_id,
        };
      }
      // Only surface the dialog if there are blockers OR warnings.
      if ((diag.blocking_reasons || []).length > 0 || (diag.warnings || []).length > 0) {
        setDiagnostic(diag);
      }
    };
    if (immediate) return run();
    if (preflightTimerRef.current) clearTimeout(preflightTimerRef.current);
    preflightTimerRef.current = setTimeout(run, 250);
  };

  useEffect(() => () => {
    if (preflightTimerRef.current) clearTimeout(preflightTimerRef.current);
  }, []);

  const assign = async (row, extra = {}) => {
    if (!tour?.start_date || !tour?.end_date) {
      return toast.error("Set Tour start and end dates before assigning drivers.");
    }
    setSelectedRow(row);
    setBusy(true);
    try {
      await api.post(`/tours/${tour.id}/drivers`, {
        driver_id: row.driver.id,
        role: "primary_driver",
        start_date: tour.start_date,
        end_date: tour.end_date,
        ...extra,
      });
      toast.success(`${row.driver.display_name} assigned`);
      setPendingWarning(null);
      setAssigning(null);
      setDiagnostic(null);
      correlationRef.current = null;
      if (refresh) await refresh();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "driver_needs_acknowledgement") {
        setPendingWarning({
          row,
          overall_status: detail.overall_status,
          warnings: detail.warning_reasons || [],
        });
      } else {
        // RC10s · atomic save rerun returned block (or any error).
        // Translate server payload into the shared RC10s diagnostic
        // envelope so the "Driver Cannot Be Assigned" dialog renders
        // every blocking reason with action buttons.
        const diag = parseSaveErrorAsDiagnostic(
          err, correlationRef.current?.correlationId,
        );
        setDiagnostic(diag);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-carbon/60 flex items-start justify-center px-2 py-6 overflow-y-auto"
         data-testid="find-drivers-modal"
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-road-white rounded-md w-full max-w-5xl border border-coach-silver shadow-lg">
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between sticky top-0">
          <div className="flex items-baseline gap-3">
            <div className="text-[14px] font-medium">Find Drivers for this Tour</div>
            <div className="text-[11px] text-coach-silver">
              {tour.tour_number ? `${tour.tour_number} · ` : ""}
              {tour.artist_display || tour.display_name || ""}
            </div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="find-drivers-close"
                  className="text-coach-silver hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="p-4">
          <DriverSearchPanel
            tourContext={{
              tour_id: tour.id,
              tour_number: tour.tour_number,
              customer_id: tour.customer_id,
              customer_display_name: tour.artist_display || tour.display_name,
              start_date: tour.start_date,
              end_date: tour.end_date,
            }}
            onRowAction={async (row) => {
              // RC10s · Preflight-on-select. Fire the canonical server
              // evaluator the moment the operator picks a driver. If
              // the preflight returns ANY blocking reason, open the
              // "Driver Cannot Be Assigned" dialog instead of
              // attempting the save (which would still fail and
              // produce the same dialog via the atomic save rerun).
              setSelectedRow(row);
              const diag = await rc10sPreflight(
                tour.id, row?.driver?.id, "primary_driver",
                correlationRef.current?.correlationId,
              );
              correlationRef.current = {
                driverId: row?.driver?.id,
                correlationId: diag.correlation_id,
              };
              if ((diag.blocking_reasons || []).length > 0) {
                setDiagnostic(diag);
                return;
              }
              if ((diag.warnings || []).length > 0 && !diag._medical_ack_required) {
                setDiagnostic(diag);
                return;
              }
              await assign(row);
            }}
            actionLabel="Assign as Main"
          />
          <div className="mt-3 text-[11px] text-steel italic">
            Eligibility is enforced server-side on every assign. Blocked drivers remain clickable — the reason is shown in a diagnostic dialog so you can resolve it or pick someone else.
          </div>
        </div>
      </div>
      <DriverCannotBeAssignedDialog
        open={!!diagnostic}
        diagnostic={diagnostic}
        driverId={selectedRow?.driver?.id}
        tourId={tour?.id}
        driverName={selectedRow?.driver?.display_name}
        onClose={() => { setDiagnostic(null); }}
        onFindReplacement={() => { setDiagnostic(null); }}
        onAcknowledgeWarning={async () => {
          setDiagnostic(null);
          if (selectedRow) await assign(selectedRow, { warnings_acknowledged: true });
        }}
      />
      {pendingWarning && (
        <WarningAcknowledgementDialog
          overallStatus={pendingWarning.overall_status}
          warnings={pendingWarning.warnings}
          busy={busy}
          onCancel={() => setPendingWarning(null)}
          onConfirm={() => assign(pendingWarning.row, { warnings_acknowledged: true })}
        />
      )}
    </div>
  );
}

// ---------------- Bus card ----------------
function BusCard({ bus, open, onToggle, subtotal, lines, trailer, busDrivers, allDrivers, tourId, tour, canWrite, canAssign, refresh }) {
  const [addTrailerOpen, setAddTrailerOpen] = useState(false);
  const [addDriverOpen, setAddDriverOpen] = useState(null); // "main" | "co" | null

  if (!bus) return null;
  const busTestId = `bus-card-${bus.assignment_id.slice(0, 8)}`;

  return (
    <div data-testid={busTestId}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-road-white"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown size={14} className="text-steel" /> : <ChevronRight size={14} className="text-steel" />}
          <BusIcon size={13} className="text-coach-blue" />
          <span className="font-medium text-[13px] text-carbon truncate">
            {bus.display || "Unassigned Entertainer Coach"}
          </span>
          {trailer && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest text-steel border border-coach-silver rounded-sm px-1.5 py-0.5">
              <Truck size={10} /> Trailer attached
            </span>
          )}
        </div>
        <div className="text-[13px] tabular text-deep-road-navy font-display">
          {fmt(subtotal || 0)}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-3">
          {lines.length === 0 ? (
            <div className="text-[12px] text-steel italic py-2 border-t border-coach-silver">
              No lines tagged to this Bus yet. Use the Pricing editor below (or add a Custom Charge tagged to this Bus).
            </div>
          ) : (
            <table className="w-full text-[12.5px] mt-1">
              <thead className="text-[10px] uppercase tracking-widest text-steel">
                <tr>
                  <th className="text-left py-1">Description</th>
                  <th className="text-right py-1 w-14">Qty</th>
                  <th className="text-left py-1 w-24">Unit</th>
                  <th className="text-right py-1 w-24">Rate</th>
                  <th className="text-right py-1 w-28">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-coach-silver/60">
                {lines.map((li) => (
                  <tr key={li.id}>
                    <td className="py-1 pr-2 text-carbon">
                      {li.customer_facing_description || li.description}
                    </td>
                    <td className="py-1 text-right tabular">{li.quantity}</td>
                    <td className="py-1 text-steel">{li.unit_of_measure || li.unit}</td>
                    <td className="py-1 text-right tabular">{fmt(li.rate)}</td>
                    <td className="py-1 text-right tabular font-medium">
                      {fmt(li.computed_amount || li.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {canAssign && !trailer && (
              <button type="button"
                onClick={() => setAddTrailerOpen(true)}
                data-testid={`${busTestId}-add-trailer`}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11.5px] border border-coach-silver text-carbon rounded-sm hover:bg-road-white">
                <Plus size={11} /> Add Trailer
              </button>
            )}
            {canAssign && (
              <button type="button"
                onClick={() => setAddDriverOpen("main")}
                data-testid={`${busTestId}-add-main-driver`}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11.5px] border border-coach-silver text-carbon rounded-sm hover:bg-road-white">
                <Users size={11} /> Add Main Driver
              </button>
            )}
            {canAssign && (
              <button type="button"
                onClick={() => setAddDriverOpen("co")}
                data-testid={`${busTestId}-add-co-driver`}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11.5px] border border-coach-silver text-carbon rounded-sm hover:bg-road-white">
                <Users size={11} /> Add Co-Driver
              </button>
            )}
          </div>

          {addTrailerOpen && (
            <AddTrailerModal
              busAssignmentId={bus.assignment_id}
              onClose={() => setAddTrailerOpen(false)}
              onDone={async () => { setAddTrailerOpen(false); await refresh(); }}
              tourId={tourId}
            />
          )}
          {addDriverOpen && (
            <AddDriverModal
              busAssignmentId={bus.assignment_id}
              driverRole={addDriverOpen}
              allDrivers={allDrivers}
              onClose={() => setAddDriverOpen(null)}
              onDone={async () => { setAddDriverOpen(null); await refresh(); }}
              tourId={tourId}
              tour={tour}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- Quote-level lines ----------------
function QuoteLevelLines({ lines }) {
  return (
    <table className="w-full text-[12px]">
      <tbody className="divide-y divide-coach-silver/60">
        {lines.map((li) => (
          <tr key={li.id}>
            <td className="py-1 px-4 text-carbon">
              {li.customer_facing_description || li.description}
              {li.percentage_of_base && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-steel">
                  · {li.rate}% of {li.percentage_of_base.replace(/_/g, " ")}
                </span>
              )}
            </td>
            <td className="py-1 text-right tabular pr-4 w-32 font-medium">
              {fmt(li.computed_amount || li.amount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------- Add Bus modal ----------------
function AddBusModal({ tour, allAssets, existingAssignedIds, onClose, onDone }) {
  const [assetId, setAssetId] = useState("");
  const [busy, setBusy] = useState(false);
  const candidates = allAssets.filter((a) => a.asset_type !== "trailer" && !existingAssignedIds.includes(a.id));
  const submit = async (e) => {
    e.preventDefault();
    if (!assetId) return toast.error("Choose a coach");
    setBusy(true);
    try {
      const asset = allAssets.find((a) => a.id === assetId) || {};
      const r = await api.post(`/tours/${tour.id}/assets`, {
        asset_id: assetId,
        start_date: tour.start_date, end_date: tour.end_date,
      });
      const newAssnId = r.data?.id;
      // Wave 4B-QC · auto-seed the new Bus with standard operational
      // and pricing lines from the current catalog + Tour context.
      if (newAssnId) {
        try {
          // Import catalog defaults lazily so this module stays small.
          const { catalogByKey } = await import("./pricingCatalog");
          const rateOf = (k) => catalogByKey(k)?.defaultRate;
          await api.post(`/tours/${tour.id}/quote-builder/seed-bus`, {
            unit_group_id: newAssnId,
            has_trailer: false,
            coach_default_rate: asset?.default_rate || null,
            catalog_defaults: {
              fuel_estimate: rateOf("fuel_estimate"),
              engine_service: rateOf("engine_service"),
              generator_service: rateOf("generator_service"),
              internet_starlink: rateOf("internet"),
            },
          });
        } catch (seedErr) {
          // Seeding failure is non-fatal — the Bus is attached and
          // operator can add lines manually. Surface a soft warning.
          toast.warning("Bus added, but auto-seed skipped some lines.");
        }
      }
      await onDone();
      toast.success("Bus added and seeded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-carbon/60 flex items-center justify-center px-4" data-testid="bus-add-bus-modal">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-md p-5 border border-coach-silver">
        <div className="text-[16px] text-carbon font-medium mb-3">Add a Bus / Unit</div>
        <label className="block mb-3 text-[12.5px]">
          <div className="text-[10px] uppercase tracking-widest text-steel mb-1">Coach</div>
          <select autoFocus required value={assetId} onChange={(e) => setAssetId(e.target.value)}
            data-testid="bus-add-bus-select"
            className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]">
            <option value="">Choose a coach…</option>
            {candidates.map((a) => (
              <option key={a.id} value={a.id}>{coachOptionLabel(a, candidates)}</option>
            ))}
          </select>
          {candidates.length === 0 && (
            <div className="text-[11px] text-steel italic mt-1">No unassigned coaches available.</div>
          )}
        </label>
        <div className="text-[11px] text-steel mb-3">
          Adding a coach here creates a new tour asset assignment (a new Bus). You can then attach a trailer + drivers scoped to this Bus.
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="px-3 py-1.5 border border-coach-silver rounded-sm text-[12.5px] text-steel">Cancel</button>
          <button type="submit" disabled={busy || !assetId}
            data-testid="bus-add-bus-submit"
            className="px-3 py-1.5 bg-coach-blue text-white rounded-sm text-[12.5px] font-medium disabled:opacity-50">
            {busy ? "Adding…" : "Add Bus"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Add Trailer modal ----------------
function AddTrailerModal({ busAssignmentId, tourId, onClose, onDone }) {
  const [trailerId, setTrailerId] = useState("");
  const [trailers, setTrailers] = useState([]);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    api.get("/assets?asset_type=trailer&active=true&page_size=50").then((r) => {
      setTrailers(r.data.results || []);
    }).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!trailerId) return toast.error("Choose a trailer");
    setBusy(true);
    try {
      await api.post(`/tours/${tourId}/assets`, {
        asset_id: trailerId,
        pairs_with_assignment_id: busAssignmentId,
      });
      await onDone();
      toast.success("Trailer attached to Bus");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-carbon/60 flex items-center justify-center px-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-md p-5 border border-coach-silver">
        <div className="text-[16px] text-carbon font-medium mb-3">Attach Trailer to this Bus</div>
        <select autoFocus required value={trailerId} onChange={(e) => setTrailerId(e.target.value)}
          className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] mb-3">
          <option value="">Choose a trailer…</option>
          {trailers.map((t) => (
            <option key={t.id} value={t.id}>{coachOptionLabel(t, trailers)}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="px-3 py-1.5 border border-coach-silver rounded-sm text-[12.5px] text-steel">Cancel</button>
          <button type="submit" disabled={busy || !trailerId}
            className="px-3 py-1.5 bg-coach-blue text-white rounded-sm text-[12.5px] font-medium disabled:opacity-50">
            {busy ? "Attaching…" : "Attach Trailer"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Add Driver modal (DAQ-1 Sub-Phase 4 aware) ----------------
function AddDriverModal({ busAssignmentId, tourId, tour, driverRole = "main", allDrivers, onClose, onDone }) {
  const [driverId, setDriverId] = useState("");
  const [driverRecord, setDriverRecord] = useState(null);
  const [role, setRole] = useState(driverRole === "co" ? "co_driver" : "primary_driver");
  const [busy, setBusy] = useState(false);
  const [pendingWarning, setPendingWarning] = useState(null); // { warnings, overall_status }
  const [hardBlock, setHardBlock] = useState(null);            // { overall_status, block_reasons }
  // RC10s Pass 2 — structured diagnostic for the "Driver Cannot Be
  // Assigned" dialog. Replaces the legacy toast fallback on this
  // per-bus Add form.
  const [busDiagnostic, setBusDiagnostic] = useState(null);
  const correlationRef = useRef(null);
  // RC10s Pass 3 — the live driver label comes from the typeahead
  // selection (not a prop-fed 100-record list). `allDrivers` is still
  // accepted for backward compatibility but is no longer consulted
  // for roster display inside this modal.
  const driverLabel = driverRecord?.display_name || driverRecord?.name || driverId;

  const postAssignment = async (extra = {}) => {
    return api.post(`/tours/${tourId}/drivers`, {
      driver_id: driverId,
      role,
      start_date: tour?.start_date,
      end_date: tour?.end_date,
      ...extra,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!driverId) return toast.error("Choose a driver");
    if (!tour?.start_date || !tour?.end_date) {
      return toast.error("Set Tour start and end dates before assigning drivers.");
    }
    setBusy(true);
    setHardBlock(null);
    try {
      await postAssignment();
      await onDone();
      toast.success(`${driverRole === "co" ? "Co-Driver" : "Main Driver"} assigned`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "driver_needs_acknowledgement") {
        setPendingWarning({
          overall_status: detail.overall_status,
          warnings: detail.warning_reasons || [],
        });
      } else {
        // RC10s Pass 2 — route save errors into the structured dialog
        // instead of a toast. Covers DRIVER_ARCHIVED / DRIVER_INACTIVE /
        // DUPLICATE_TOUR_ASSIGNMENT / all other reason codes.
        const diag = parseSaveErrorAsDiagnostic(err, correlationRef.current?.correlationId);
        setBusDiagnostic({ diag, driverId, driverName: driverLabel });
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmWarnings = async () => {
    setBusy(true);
    try {
      await postAssignment({ warnings_acknowledged: true });
      await onDone();
      toast.success(`${driverRole === "co" ? "Co-Driver" : "Main Driver"} assigned (warnings acknowledged)`);
    } catch (err) {
      const diag = parseSaveErrorAsDiagnostic(err, correlationRef.current?.correlationId);
      setBusDiagnostic({ diag, driverId, driverName: driverLabel });
    } finally {
      setBusy(false);
      setPendingWarning(null);
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-50 bg-carbon/60 flex items-center justify-center px-4"
         data-testid="add-driver-modal">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-md p-5 border border-coach-silver">
        <div className="text-[16px] text-carbon font-medium mb-3">
          Add {driverRole === "co" ? "Co-Driver" : "Main Driver"} to this Bus
        </div>
        <label className="block mb-2 text-[12.5px]">
          <div className="text-[10px] uppercase tracking-widest text-steel mb-1">Driver</div>
          <DriverTypeahead
            value={driverId}
            onSelect={(d) => {
              setDriverId(d ? d.id : "");
              setDriverRecord(d);
              setHardBlock(null);
            }}
            testIdPrefix="add-driver-typeahead"
            autoFocus
          />
        </label>
        <div className="text-[11px] text-steel mb-3">
          Eligibility (DO NOT ASSIGN, availability, credential expirations, tour conflicts) is enforced server-side before this write commits.
        </div>

        {hardBlock && (
          <div className="mb-3 border border-critical-red bg-critical-red/5 rounded-sm p-2.5 text-[12px]"
               data-testid="add-driver-hardblock">
            <div className="text-critical-red font-medium uppercase tracking-widest text-[10.5px] mb-1">
              Cannot assign — {hardBlock.overall_status.replace(/_/g, " ")}
            </div>
            <ul className="list-disc pl-5 space-y-0.5 text-carbon">
              {hardBlock.block_reasons.map((r, i) => (
                <li key={i}><span className="font-mono text-[11px]">{r.code}</span> — {r.message}</li>
              ))}
            </ul>
            <div className="mt-1.5 text-[11px] text-steel italic">
              Resolve the underlying condition (remove the restriction, adjust availability, renew credential, or unassign the conflicting tour). No override is available.
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="px-3 py-1.5 border border-coach-silver rounded-sm text-[12.5px] text-steel">Cancel</button>
          <button type="submit" disabled={busy || !driverId}
            data-testid="add-driver-submit"
            className="px-3 py-1.5 bg-coach-blue text-white rounded-sm text-[12.5px] font-medium disabled:opacity-50">
            {busy ? "Checking…" : "Add"}
          </button>
        </div>
      </form>
      {pendingWarning && (
        <WarningAcknowledgementDialog
          overallStatus={pendingWarning.overall_status}
          warnings={pendingWarning.warnings}
          busy={busy}
          onCancel={() => setPendingWarning(null)}
          onConfirm={confirmWarnings}
        />
      )}
    </div>
      {busDiagnostic && (
        <DriverCannotBeAssignedDialog
          open={!!busDiagnostic}
          diagnostic={busDiagnostic.diag}
          driverId={busDiagnostic.driverId}
          tourId={tourId}
          driverName={busDiagnostic.driverName}
          onClose={() => setBusDiagnostic(null)}
          onFindReplacement={() => { setBusDiagnostic(null); setDriverId(""); }}
        />
      )}
    </>
  );
}

function WarningAcknowledgementDialog({ overallStatus, warnings, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] bg-carbon/70 flex items-center justify-center px-4"
         data-testid="warning-ack-dialog">
      <div className="bg-white rounded-md w-full max-w-md border border-amber-hold">
        <div className="bg-amber-hold/15 border-b border-amber-hold/40 px-4 py-3">
          <div className="text-[10.5px] uppercase tracking-widest text-amber-hold font-medium">
            Warning — {overallStatus.replace(/_/g, " ")}
          </div>
          <div className="text-[13px] text-carbon mt-0.5">
            This assignment can proceed, but the operator must acknowledge the following:
          </div>
        </div>
        <ul className="p-4 space-y-1.5 text-[12.5px] text-carbon">
          {warnings.map((w, i) => (
            <li key={i} data-testid={`warning-item-${w.code}`} className="flex items-start gap-2">
              <span className="font-mono text-[11px] text-amber-hold">{w.code}</span>
              <span className="text-steel">{w.message}</span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-coach-silver bg-road-white">
          <button type="button" onClick={onCancel} disabled={busy}
                  data-testid="warning-ack-cancel"
                  className="px-3 py-1.5 border border-coach-silver rounded-sm text-[12.5px] text-steel">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy}
                  data-testid="warning-ack-confirm"
                  className="px-3 py-1.5 bg-amber-hold text-white rounded-sm text-[12.5px] font-medium disabled:opacity-50">
            {busy ? "Assigning…" : "Acknowledge & Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
